import { useLayoutEffect, useState } from "react";

import { logError } from "../../service/error";

interface UseIsTextTruncatedReturnData {
  /** Callback ref for the element whose text is cut with `text-overflow: ellipsis`. */
  ref: (node: HTMLElement | null) => void;
  /** Whether the element's text currently overflows its box. */
  isTruncated: boolean;
}

/**
 * Reports whether an ellipsised element is actually cutting its text, so a tooltip revealing the
 * full text is only rendered when there is something hidden to reveal.
 *
 * `text` is only a dependency: a new string in the same box has to be measured again.
 */
export const useIsTextTruncated = (text: string): UseIsTextTruncatedReturnData => {
  // Holding the node in state rather than a ref: the measurement has to follow whichever element
  // is actually on the page, not the one that was there when it first ran.
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    if (!node) return undefined;

    // No tolerance: the browser draws the ellipsis on a one-pixel overflow, and text showing "…"
    // with no way to read it is the failure this is here to prevent.
    const measure = (): void => setIsTruncated(node.scrollWidth > node.clientWidth);

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);

    // A web font swapping in changes how wide the text is without changing the box it sits in, so
    // the observer never fires — text measured against the fallback font would silently keep a
    // truncation nobody can read.
    let isStale = false;
    document.fonts?.ready
      .then(() => {
        if (!isStale) measure();
      })
      .catch((error) => logError({ error }));

    return () => {
      isStale = true;
      observer.disconnect();
    };
  }, [node, text]);

  return { ref: setNode, isTruncated };
};
